/**
 * `BandLegend` — the demo's `bandBar()` (line 2836): a stacked distribution bar
 * over the five condition bands with a labelled key under it.
 *
 * The bar's segments are weighted by machine count (the demo's `flex:n`), so a
 * band with no machines contributes nothing; the key below still lists every
 * band so the scale reads as a scale, not as a sample.
 *
 * UI layer — the counts arrive already derived.
 *
 * @see src/versions/v1/modules/fleet/FleetHealthScene.transform.ts
 */

import type { BandLegendEntry } from './FleetHealthScene.transform';

export interface BandLegendProps {
  entries: readonly BandLegendEntry[];
  /** Line under the key, e.g. `'condition band = the machine's lowest component score'`. */
  caption?: string;
}

export function BandLegend({ entries, caption }: BandLegendProps) {
  const present = entries.filter((e) => e.count > 0);

  return (
    <div className="max-w-xl">
      <div className="band-bar" role="img" aria-label="Fleet condition distribution">
        {present.map((e) => (
          <i
            key={e.band.key}
            style={{ flex: e.count, background: `var(--band-${e.band.key})` }}
            title={`${e.count} ${e.band.label.toLowerCase()}`}
          />
        ))}
      </div>
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
        {entries.map((e) => (
          <li key={e.band.key} className="inline-flex items-center gap-1.5">
            <span className={`band-dot band-${e.band.key}`} aria-hidden="true" />
            <span>
              {e.band.label} <span className="mono">{e.count}</span>
            </span>
          </li>
        ))}
      </ul>
      {caption ? <p className="mt-1 text-[11px] text-muted">{caption}</p> : null}
    </div>
  );
}
